import React, { useMemo, useState } from 'react';
import { IconButton, Badge, Popover, Box, Typography, List, ListItemButton, ListItemText, Divider, CircularProgress } from '@mui/material';
import { Notifications, ChevronRight } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/constants';
import { useNotificationBadgeCounts } from '@hooks/useNotificationBadgeCounts';
import { useNotificationBadgeCountsByClient } from '@hooks/useNotificationBadgeCountsByClient';
import { useDemands } from '@contexts/DemandsContext';
import { sumPendingInOtherClients } from './clientSelectorHelpers';

interface BadgeRow {
  key: string;
  label: string;
  count: number | null;
  color: string;
  onClick: () => void;
}

/**
 * Sino de notificações (pedido do usuário, 05/09) — mesmo componente conceitual do mobile
 * (NotificationBell.tsx lá), consumindo a mesma rota `GET /api/admin/notifications/badge-counts`.
 * Clique abre um painel com a contagem por categoria + atalho pra tela correspondente — não uma
 * lista item-a-item (decisão documentada no plano: evita construir persistência de notificação
 * individual, que não foi pedida).
 *
 * Cada linha só aparece quando o backend devolveu um número pra ela (não `null`) — o backend já
 * zera (null) a categoria inteira quando o papel do usuário não tem a permissão correspondente
 * (achado real, relato do usuário: "as opções ao clicar no sino são sempre exibidas independente
 * das permissões do papel"), então não há necessidade de duplicar a checagem de permissão aqui.
 *
 * Navegação (pedido do usuário, 05/09) — cada categoria leva ao board de Demandas (ou Sugestões)
 * JÁ FILTRADO pra mostrar só o que aquela categoria representa, via query params consumidos por
 * `useNotificationFilterFromUrl` (Demandas) e `SuggestionsReportTab` (Sugestões).
 */
const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const { counts, loading, error, refetch } = useNotificationBadgeCounts();
  const { countsByClient } = useNotificationBadgeCountsByClient();
  const { selectedClients } = useDemands();
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);

  // Pedido do usuário (10/09): "exiba uma linha... informando que existem X pendências em outros
  // condomínios... o X não deve contar as pendências do condomínio filtrado no momento" — as
  // linhas acima já são escopadas ao condomínio filtrado (ou ao portfólio inteiro com "Todos"), e
  // este total soma só o que fica FORA dessa contagem. Só faz sentido com exatamente 1 condomínio
  // filtrado — com "Todos" selecionado, não existe "fora" (o sino já mostra tudo).
  const selectedClientId = selectedClients[0];
  const otherClientsPendingTotal = useMemo(
    () => sumPendingInOtherClients(countsByClient ?? undefined, selectedClientId),
    [countsByClient, selectedClientId]
  );

  const goTo = (path: string) => {
    setAnchorEl(null);
    navigate(path);
  };

  const rows: BadgeRow[] = counts
    ? [
        { key: 'open', label: 'Demandas abertas', count: counts.openCount, color: '#d32f2f', onClick: () => goTo(`${ROUTES.APP}?status=pending`) },
        { key: 'inProgress', label: 'Demandas em andamento', count: counts.inProgressCount, color: '#0288d1', onClick: () => goTo(`${ROUTES.APP}?status=in_progress`) },
        { key: 'overdue', label: 'Demandas atrasadas', count: counts.overdueCount, color: '#c62828', onClick: () => goTo(`${ROUTES.APP}?overdue=1`) },
        // Pedido do usuário (14/09): Sugestões virou a aba "Sugestões" de Painel Público — aponta
        // direto pra lá (evita o hop de redirect de ROUTES.APP_SUGGESTIONS, mantido só por
        // compatibilidade com links antigos, ver routes.tsx).
        { key: 'suggestions', label: 'Sugestões pendentes', count: counts.pendingSuggestionsCount, color: '#7b1fa2', onClick: () => goTo(`${ROUTES.APP_PUBLIC_PANEL}?tab=suggestions&status=novo`) },
        { key: 'checklists', label: 'Checklists pendentes', count: counts.pendingChecklistsCount, color: '#ed6c02', onClick: () => goTo(`${ROUTES.APP}?checklistPending=1`) },
        { key: 'patrimony', label: 'Patrimônio com divergência', count: counts.patrimonyDivergenceCount, color: '#5d4037', onClick: () => goTo(`${ROUTES.APP_PATRIMONY}?divergence=1`) },
        // Fase 5 (setembro/2026): demandas com prestador WhatsApp vinculado ainda sem o gestor ter
        // disparado o envio — subconjunto de "Demandas abertas", cor do WhatsApp pra ficar óbvio.
        { key: 'whatsappPending', label: 'Prestadores aguardando envio de WhatsApp', count: counts.whatsappPendingCount, color: '#25D366', onClick: () => goTo(`${ROUTES.APP}?whatsappPending=1`) },
      ]
    : [];
  const visibleRows = rows.filter((row) => row.count !== null && row.count > 0);

  return (
    <>
      <IconButton
        size="large"
        aria-label="notificações"
        onClick={(e) => {
          setAnchorEl(e.currentTarget);
          // Busca fresca a cada abertura (não confia só no refetch periódico de 60s) — pedido do
          // usuário: "preciso de total consistência" entre web e mobile, que compartilham o mesmo
          // endpoint mas podiam mostrar números de momentos diferentes.
          void refetch();
        }}
        color="inherit"
        sx={{ mr: { xs: 0.5, sm: 1 }, color: '#334155' }}
      >
        <Badge badgeContent={counts?.total ?? 0} color="error" max={99}>
          <Notifications />
        </Badge>
      </IconButton>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ width: 320, maxWidth: '90vw' }}>
          <Typography variant="subtitle1" sx={{ px: 2, pt: 1.5, pb: 1, fontWeight: 600 }}>
            Notificações
          </Typography>
          <Divider />
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
            </Box>
          ) : error ? (
            <Typography variant="body2" color="error" sx={{ p: 2 }}>
              {error}
            </Typography>
          ) : visibleRows.length === 0 && otherClientsPendingTotal === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
              Nenhuma pendência no momento.
            </Typography>
          ) : (
            <>
              {visibleRows.length > 0 && (
                <List sx={{ py: 0 }}>
                  {visibleRows.map((row) => (
                    <ListItemButton key={row.key} onClick={row.onClick}>
                      <ListItemText primary={row.label} primaryTypographyProps={{ variant: 'body2' }} />
                      <Typography variant="subtitle2" sx={{ color: row.color, fontWeight: 700, mr: 0.5 }}>
                        {row.count}
                      </Typography>
                      <ChevronRight fontSize="small" sx={{ color: 'text.secondary' }} />
                    </ListItemButton>
                  ))}
                </List>
              )}
              {otherClientsPendingTotal > 0 && (
                <>
                  <Divider />
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, py: 1.5 }}>
                    <Typography variant="body2" color="text.secondary">
                      Pendências em outros condomínios
                    </Typography>
                    <Box
                      sx={{
                        minWidth: 22,
                        height: 22,
                        px: 0.75,
                        borderRadius: '11px',
                        backgroundColor: 'error.main',
                        color: 'error.contrastText',
                        fontSize: 12,
                        fontWeight: 700,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {otherClientsPendingTotal}
                    </Box>
                  </Box>
                </>
              )}
            </>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default NotificationBell;
